import * as PIXI from 'pixi.js'
import { spritesheets } from '@/assets/final'
import { buildAssetUrl } from '@/utils/AssetsUrlUtil'

type TSpritesheetVariant = {
    json?: string | string[]
}

type TSpritesheetEntry = {
    hd?: TSpritesheetVariant
    sd?: TSpritesheetVariant
}

const isLikelyMobileDevice = (): boolean => {
    if (typeof window === 'undefined') return false

    try {
        if (window.matchMedia?.('(hover: none)').matches) {
            return true
        }

        return (window.navigator?.maxTouchPoints || 0) > 0
    } catch {
        return false
    }
}

const toArray = (value?: string | string[]): string[] => {
    if (!value) return []
    return Array.isArray(value) ? value : [value]
}

const normalizeLegacyZeroSuffix = (relativePath: string): string => relativePath.replace(/-0\.json$/i, '.json')

const sanitizeJsonFiles = (jsonFiles: string[]): string[] => {
    const normalized = jsonFiles.map(normalizeLegacyZeroSuffix)
    const seen = new Set<string>()
    const result: string[] = []

    for (const filePath of normalized) {
        if (seen.has(filePath)) continue
        seen.add(filePath)
        result.push(filePath)
    }

    return result
}

const normalizeRelativePath = (relativePath: string): string => relativePath.replace(/^\.\//, '')

const toSpritesheetUrl = (relativePath: string): string => buildAssetUrl(`/assets/spritesheets/${normalizeRelativePath(relativePath)}`)

const normalizeLookupKey = (value: string): string =>
    String(value || '')
        .replace(/^file:\/\//i, '')
        .replace(/^\.\//, '')
        .split('?')[0]
        .replace(/^\//, '')
        .toLowerCase()

const getAssetsCacheEntries = (): Array<[string, any]> => {
    const cacheStore: any = (PIXI as any).Assets?.cache?._cache

    if (!cacheStore) return []
    if (typeof cacheStore.entries === 'function') {
        return Array.from(cacheStore.entries()) as Array<[string, any]>
    }

    return Object.entries(cacheStore)
}

const getResolverKeys = (): string[] => {
    const assetMap: any = (PIXI as any).Assets?.resolver?._assetMap

    if (!assetMap) return []
    if (typeof assetMap.keys === 'function') {
        return Array.from(assetMap.keys()) as string[]
    }

    return Object.keys(assetMap)
}

const findExistingAsset = (candidates: string[]): any => {
    const normalizedCandidates = Array.from(new Set(candidates.filter(Boolean).map(normalizeLookupKey)))
    const cacheEntries = getAssetsCacheEntries()

    for (const candidate of normalizedCandidates) {
        const match = cacheEntries.find(([cacheKey]) => {
            const normalizedKey = normalizeLookupKey(cacheKey)
            return (
                normalizedKey === candidate ||
                normalizedKey.endsWith(`/${candidate}`) ||
                candidate.endsWith(`/${normalizedKey}`)
            )
        })

        if (match) return match[1]
    }

    return null
}

const hasResolverKey = (candidates: string[]): boolean => {
    const resolverKeys = getResolverKeys().map(normalizeLookupKey)

    return candidates.filter(Boolean).some((candidate) => {
        const normalizedCandidate = normalizeLookupKey(candidate)
        return resolverKeys.some(
            (resolverKey) =>
                resolverKey === normalizedCandidate ||
                resolverKey.endsWith(`/${normalizedCandidate}`) ||
                normalizedCandidate.endsWith(`/${resolverKey}`)
        )
    })
}

const setCacheAlias = (alias: string, asset: any): void => {
    if (!alias || asset == null) return

    try {
        ;(PIXI as any).Assets?.cache?.set?.(alias, asset)
    } catch {
        // ignore cache adapter issues
    }
}

const addAssetAlias = (alias: string | string[], src: string | string[]): void => {
    const aliases = Array.isArray(alias) ? alias : [alias]
    const sources = Array.isArray(src) ? src : [src]

    if (hasResolverKey([...aliases, ...sources])) {
        return
    }

    try {
        PIXI.Assets.add({ alias, src })
    } catch {
        // Alias may already be registered when the loader runs more than once.
    }
}

const registerSpritesheetImageFallback = (jsonUrl: string): void => {
    if (!jsonUrl.endsWith('.json')) return

    const pngUrl = jsonUrl.replace(/\.json(\?.*)?$/i, '.png$1')
    const webpUrl = pngUrl.replace(/\.png(\?.*)?$/i, '.webp$1')
    const pngFileName = pngUrl.split('/').pop() || pngUrl

    addAssetAlias([pngFileName, pngUrl], [webpUrl, pngUrl])
}

const isHalfResTexture = (texture: any): boolean => {
    const values = [
        texture?.label,
        texture?.source?.label,
        texture?.source?.resource?.src,
        texture?.source?.resource?.url,
        texture?.baseTexture?.label,
        texture?.baseTexture?.resource?.src,
        texture?.baseTexture?.resource?.url,
        ...(Array.isArray(texture?.textureCacheIds) ? texture.textureCacheIds : []),
    ]
        .filter(Boolean)
        .map((value) => String(value).toLowerCase())

    return values.some((value) => value.includes('@0.5x-0'))
}

const normalizeHalfResSpritesheet = (asset: any): void => {
    const textures = asset?.textures
    if (!textures || typeof textures !== 'object') return

    const firstTexture = Object.values(textures)[0] as any
    if (!firstTexture || !isHalfResTexture(firstTexture)) return

    const seenSources = new Set<any>()

    for (const texture of Object.values(textures) as any[]) {
        const source: any = texture?.source || texture?.baseTexture
        if (!source || seenSources.has(source)) continue

        seenSources.add(source)

        const currentResolution = Number(source.resolution)
        if (Number.isFinite(currentResolution) && currentResolution !== 0.5) {
            source.resolution = 0.5
        }

        if (typeof source.update === 'function') {
            try {
                source.update()
            } catch {
                // ignore source update issues
            }
        }
    }
}

const registerSpritesheetFramesToCache = (loadedAssets: any): void => {
    if (!loadedAssets || typeof loadedAssets !== 'object') return

    const textureCtor: any = (PIXI as any).Texture
    const assetsCache: any = (PIXI as any).Assets?.cache

    for (const asset of Object.values(loadedAssets)) {
        normalizeHalfResSpritesheet(asset)
        const textures = (asset as any)?.textures
        if (!textures || typeof textures !== 'object') continue

        for (const [frameKey, texture] of Object.entries(textures)) {
            if (!texture) continue

            try {
                if (typeof assetsCache?.set === 'function') {
                    assetsCache.set(frameKey, texture)
                }
            } catch {
                // ignore cache adapter issues
            }

            try {
                if (typeof textureCtor?.addToCache === 'function') {
                    textureCtor.addToCache(texture, frameKey)
                }
            } catch {
                // ignore texture cache issues
            }
        }
    }
}

const getPreferredVariant = (entry: TSpritesheetEntry): TSpritesheetVariant | undefined => {
    if (isLikelyMobileDevice()) {
        return entry.sd ?? entry.hd
    }

    return entry.hd ?? entry.sd
}

const getPrimaryAlias = (sheetName: string, jsonFiles: string[], jsonFile: string, index: number): string => {
    if (jsonFiles.length === 1) {
        return sheetName
    }

    const fileName = normalizeRelativePath(jsonFile).split('/').pop()?.replace(/\.json$/i, '')
    return fileName || `${sheetName}_${index}`
}

export const loadGeneratedSpritesheets = async (progressCallback?: (progress: number) => void): Promise<void> => {
    const assetAliasesToLoad: string[] = []
    const alreadyLoadedAssets: Record<string, any> = {}

    for (const [sheetName, rawEntry] of Object.entries(spritesheets as Record<string, TSpritesheetEntry>)) {
        if (sheetName.startsWith('.')) continue

        const entry = rawEntry as TSpritesheetEntry
        const variant = getPreferredVariant(entry)
        const jsonFiles = sanitizeJsonFiles(toArray(variant?.json))

        jsonFiles.forEach((jsonFile, index) => {
            const jsonUrl = toSpritesheetUrl(jsonFile)
            const alias = getPrimaryAlias(sheetName, jsonFiles, jsonFile, index)
            const jsonFileName = normalizeRelativePath(jsonFile).split('/').pop() || jsonFile
            const existingAsset = findExistingAsset([alias, jsonUrl, jsonFileName])

            if (existingAsset) {
                setCacheAlias(alias, existingAsset)
                alreadyLoadedAssets[alias] = existingAsset
                return
            }

            registerSpritesheetImageFallback(jsonUrl)

            // Register only the logical alias to avoid resolver overwrites on full URL keys.
            addAssetAlias(alias, jsonUrl)

            assetAliasesToLoad.push(alias)
        })
    }

    if (assetAliasesToLoad.length === 0) {
        registerSpritesheetFramesToCache(alreadyLoadedAssets)
        progressCallback?.(1)
        return
    }

    const loadedAssets = await PIXI.Assets.load(assetAliasesToLoad, (progress) => {
        if (progressCallback) progressCallback(progress)
    })

    registerSpritesheetFramesToCache({
        ...alreadyLoadedAssets,
        ...(loadedAssets || {}),
    })
    progressCallback?.(1)
}